import Quill from "quill";
// @ts-expect-error quilljs-markdown is not typed
import QuillMarkdown from "quilljs-markdown";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { twJoin } from "tailwind-merge";
import { useEventListener } from "usehooks-ts";

import { mdService } from "src/base-components/TextEditor/MarkdownService";
import "src/base-components/TextEditor/styles.css";

type Range = { index: number; length: number };

type Props = {
  dataLoc?: string;
  toolbarRef?: React.MutableRefObject<Nullable<HTMLUListElement>>;
  placeholder?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  keepEmptyLines?: boolean;
  value: string;
  fullHeight?: boolean;
  inline?: boolean;
  maxHeight?: `max-h-[${number}px]`;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onReady?: (quill: Quill) => void;
};

const defaultMaxHeight = "max-h-[300px]";

const mdIgnoreTags = [
  "header",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "checkbox",
];

export type TextEditorRef = {
  quill: Quill | null;
  focus: () => void;
};

// quilljs-markdown is not typed, we had to use any here
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Code: any = Quill.import("formats/code");
Code.className = "ql-code-highlighting";
Quill.register(Code, true);
Quill.register("modules/QuillMarkdown", QuillMarkdown, true);

export const TextEditor = forwardRef<TextEditorRef, Props>(
  (
    {
      dataLoc,
      placeholder,
      readOnly = false,
      autoFocus = false,
      keepEmptyLines = false,
      inline = false,
      value: propsValue,
      onChange,
      onFocus,
      onBlur,
      onReady,
      fullHeight,
      toolbarRef,
      maxHeight,
    },
    ref,
  ) => {
    const quill = useRef<Nullable<Quill>>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      quill: quill.current,
      focus: () => {
        quill.current?.focus();
      },
    }));

    const syncUserValueWithProps = useCallback(() => {
      const { current } = quill;
      if (!current) {
        return;
      }

      const html = mdService.toHtml(propsValue);

      // If the HTML is the same, we do not need to update the editor
      if (isEqualHtml(html, current.getSemanticHTML())) {
        return;
      }

      // Save current cursor position before updating content
      const currentSelection = current.getSelection();

      const delta = current.clipboard.convert({
        html,
      });
      current.setContents(delta);

      if (readOnly || !current.hasFocus()) {
        return;
      }

      // Restore cursor position, or set to end if previous position is invalid
      if (currentSelection) {
        const newLength = current.getLength();
        const restoredIndex = Math.min(
          currentSelection.index,
          newLength > 0 ? newLength - 1 : 0,
        );
        current.setSelection(restoredIndex, 0);
      } else {
        current.setSelection(current.getLength(), 0);
      }
    }, [propsValue, quill, readOnly]);

    const commitValue = useCallback(() => {
      if (onChange) {
        const html = quill.current?.getSemanticHTML() || "";
        const markdown = mdService.toMarkdown(html, { keepEmptyLines });

        onChange(markdown);
      }
    }, [onChange, keepEmptyLines]);

    useEffect(() => {
      // hack to sync value in next render cycle, othewise it doesn't work as expected
      Promise.resolve().then(() => {
        syncUserValueWithProps();
      });
    }, [propsValue, syncUserValueWithProps]);

    useEffect(() => {
      quill.current?.enable(!readOnly);
    }, [readOnly]);

    useEffect(() => {
      if (quill.current || !containerRef.current) {
        return;
      }

      const current = new Quill(containerRef.current, {
        placeholder,
        readOnly,
        modules: {
          toolbar: toolbarRef?.current,
          QuillMarkdown: { ignoreTags: mdIgnoreTags },
        },
      });

      quill.current = current;
      onReady?.(current);

      if (autoFocus && current) {
        setTimeout(() => {
          current.setSelection(current.getLength(), 0);
        });
      }
    }, [autoFocus, placeholder, toolbarRef, readOnly, onChange, onReady]);

    useEffect(() => {
      const handleSelectionChange = (range: Range, oldRange: Range) => {
        if (range === null && oldRange !== null) {
          onBlur?.();
        }

        if (range !== null && oldRange === null) {
          onFocus?.();
        }
      };

      quill.current?.on("selection-change", handleSelectionChange);

      return () => {
        quill.current?.off("selection-change", handleSelectionChange);
      };
    }, [quill, onFocus, onBlur]);

    useEffect(() => {
      // Commit value when the editor container
      // loses focus, this happens when the user clicks outside the editor
      // or when the user clicks on the toolbar
      const handleFocusOut = () => {
        commitValue();
      };

      const container = quill.current?.container;
      if (container) {
        container.addEventListener("focusout", handleFocusOut);
      }

      return () => {
        if (container) {
          container.removeEventListener("focusout", handleFocusOut);
        }
      };
    }, [quill, commitValue]);

    // Commit value on outside click
    useEventListener("mousedown", (e) => {
      const [container, toolbar] = [containerRef.current, toolbarRef?.current];

      if (
        container?.contains(document.activeElement) &&
        !(
          container?.contains(e.target as Node) ||
          toolbar?.contains(e.target as Node)
        )
      ) {
        commitValue();
      }
    });

    return (
      <div
        ref={containerRef}
        className={twJoin(
          "decideScrollbar relative overflow-y-auto whitespace-pre-wrap text-gray-800 font-inter-normal-13px",
          inline ? "inline-flex max-w-full" : "flex w-full",
          !readOnly && "min-h-[50px]",
          fullHeight && "h-full",
          maxHeight ? maxHeight : !readOnly && defaultMaxHeight,
        )}
        data-loc={dataLoc}
      />
    );
  },
);

/**
 *  We do not want to compare large HTML strings, because it can be slow and
 *  it can cause performance issues. We just consider them notEqual in that case
 */

const isEqualHtml = (html1: string, html2: string) => {
  const SIZE_THRESHOLD = 10_000;

  if (html1.length > SIZE_THRESHOLD || html2.length > SIZE_THRESHOLD) {
    return false;
  }

  return html1.replace(/\n/g, "").trim() === html2.replace(/\n/g, "").trim();
};
